import React, { useState } from "react";
import PropTypes from "prop-types";
import { getMessage } from "../../../../data/locales";
import CourseDownloadableToggle from "../../../../components/CourseDownloadableToggle";
import TemarioContenidoEditor from "../../../../components/TemarioContenidoEditor";
import TemarioContenidoPreview from "../../../../components/TemarioContenidoPreview";
import AdminDocsTestsByTab from "../../../../components/AdminDocsTestsByTab";
import CourseMaterialEmbedModal from "../../../../components/CourseMaterialEmbedModal";
import { useCourseMaterialEmbed } from "../../../../hooks/useCourseMaterialEmbed";
import { listPathOptions } from "../../../../utils/temarioTree";
import { removeArchivoFromTemarioState } from "../../../../utils/courseTemarioArchivoRemove";
import { TEMARIO_DEPTH_LABELS } from "./editarCursoConstants";
import { uploadPhaseMessage } from "./editarCursoUiStrings";
import {
  getCourseMaterialMaxFileLabel,
  getCourseMaterialOversizedSelectionMessage,
  isCourseMaterialFileOversized,
} from "../../../../constants/courseMaterialUploadLimits";
import { getNamedCourseTabs, hasUnnamedCourseTabs } from "../../../../utils/normalizeCourseShape";
import ViewportSelect from "../../../../components/ViewportSelect";
import { downloadCourseMaterial } from "../../../../utils/downloadCourseMaterial";

const TITLE_MANAGE_CONTENT = "Requiere permiso para gestionar contenido";

const DOC_UPLOAD_ALLOWED_EXTENSIONS = new Set([
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".mp3",
  ".wav",
  ".mp4",
  ".webm",
]);

const DOC_UPLOAD_ACCEPT_ATTR = [
  ...Array.from(DOC_UPLOAD_ALLOWED_EXTENSIONS).sort((a, b) => a.localeCompare(b)),
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "audio/*",
  "video/*",
].join(",");

function fileMatchesDocUploadAccept(file) {
  if (!(file instanceof File)) {
    return false;
  }
  const lowerName = typeof file.name === "string" ? file.name.toLowerCase() : "";
  const dot = lowerName.lastIndexOf(".");
  const ext = dot >= 0 ? lowerName.slice(dot) : "";
  if (ext && DOC_UPLOAD_ALLOWED_EXTENSIONS.has(ext)) {
    return true;
  }
  const t = typeof file.type === "string" ? file.type.toLowerCase() : "";
  if (t === "application/pdf") {
    return true;
  }
  if (t === "application/msword" || t.includes("wordprocessingml")) {
    return true;
  }
  if (t.includes("ms-excel") || t.includes("spreadsheetml")) {
    return true;
  }
  if (t.startsWith("audio/") || t.startsWith("video/")) {
    return true;
  }
  return false;
}

function formatFileSize(bytes) {
  if (typeof bytes !== "number" || !Number.isFinite(bytes) || bytes < 0) {
    return "";
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function DocsTabIntroHeader() {
  return (
    <>
      <header className="cc-docs-tab-page-header">
        <h1>Documentos del curso</h1>
        <p className="cc-docs-tab-lead">
          Aquí enlazas PDFs y otros materiales al <strong>temario por pestañas</strong>. Los cambios no son definitivos hasta que
          pulses <strong>Guardar cambios</strong> en el menú lateral.
        </p>
      </header>

      <div className="cc-docs-tab-intro" role="note">
        <strong className="cc-docs-tab-intro-title">Cómo usar esta pantalla</strong>
        <ol className="cc-docs-tab-intro-list">
          <li>
            <span className="cc-docs-tab-intro-step-num">1</span>
            <span className="cc-docs-tab-intro-step-text">
              Indica <strong>pestaña</strong> y <strong>dónde</strong> va el archivo (nuevo apartado o uno que ya existe).
            </span>
          </li>
          <li>
            <span className="cc-docs-tab-intro-step-num">2</span>
            <span className="cc-docs-tab-intro-step-text">
              Elige el archivo y pulsa <strong>Subir y asociar al temario</strong>.
            </span>
          </li>
          <li>
            <span className="cc-docs-tab-intro-step-num">3</span>
            <span className="cc-docs-tab-intro-step-text">
              Revisa y ordena en el temario abajo; usa el resumen para ver todas las pestañas a la vez.
            </span>
          </li>
        </ol>
      </div>
    </>
  );
}

function DocsTabTemarioPlacementFields({
  tipoInsert,
  contentDisabled,
  bloqueSeleccionado,
  bloquesContenido,
  rutaPadreKey,
  setRutaPadreKey,
  rutaDestinoKey,
  setRutaDestinoKey,
  subseccion,
  setSubseccion,
}) {
  if (tipoInsert === "nuevo") {
    return (
      <div className="cc-doc-form-grid">
        <div className="form-group cc-doc-field">
          <label htmlFor="editar-syllabus-padre">Colgar el apartado nuevo bajo…</label>
          <ViewportSelect
            id="editar-syllabus-padre"
            className="form-control"
            value={rutaPadreKey}
            onChange={(ev) => setRutaPadreKey(ev.target.value)}
            disabled={contentDisabled || !bloqueSeleccionado}
            title={contentDisabled ? TITLE_MANAGE_CONTENT : undefined}
          >
            <option value="">Raíz de la pestaña (mismo nivel que los bloques actuales)</option>
            {listPathOptions(bloquesContenido[bloqueSeleccionado] || [], TEMARIO_DEPTH_LABELS).map((o) => (
              <option key={o.pathKey} value={o.pathKey}>
                {o.label}
              </option>
            ))}
          </ViewportSelect>
        </div>
        <div className="form-group cc-doc-field cc-doc-field--full">
          <label htmlFor="editar-syllabus-nuevo-nombre">Nombre del apartado nuevo</label>
          <input
            id="editar-syllabus-nuevo-nombre"
            className="form-control"
            value={subseccion}
            onChange={(ev) => setSubseccion(ev.target.value)}
            disabled={contentDisabled}
            title={contentDisabled ? TITLE_MANAGE_CONTENT : undefined}
            placeholder="Ej. Unidad 2 — Práctica"
          />
        </div>
      </div>
    );
  }
  return (
    <div className="form-group cc-doc-field" style={{ marginBottom: 18 }}>
      <label htmlFor="editar-syllabus-ruta-destino">Apartado del temario donde añadir el archivo</label>
      <ViewportSelect
        id="editar-syllabus-ruta-destino"
        className="form-control"
        value={rutaDestinoKey}
        onChange={(ev) => setRutaDestinoKey(ev.target.value)}
        disabled={contentDisabled || !bloqueSeleccionado}
        title={contentDisabled ? TITLE_MANAGE_CONTENT : undefined}
      >
        <option value="">— Elige un bloque o tema del temario —</option>
        {listPathOptions(bloquesContenido[bloqueSeleccionado] || [], TEMARIO_DEPTH_LABELS).map((o) => (
          <option key={o.pathKey} value={o.pathKey}>
            {o.label}
          </option>
        ))}
      </ViewportSelect>
    </div>
  );
}

DocsTabTemarioPlacementFields.propTypes = {
  tipoInsert: PropTypes.string.isRequired,
  contentDisabled: PropTypes.bool.isRequired,
  bloqueSeleccionado: PropTypes.string.isRequired,
  bloquesContenido: PropTypes.objectOf(PropTypes.array).isRequired,
  rutaPadreKey: PropTypes.string.isRequired,
  setRutaPadreKey: PropTypes.func.isRequired,
  rutaDestinoKey: PropTypes.string.isRequired,
  setRutaDestinoKey: PropTypes.func.isRequired,
  subseccion: PropTypes.string.isRequired,
  setSubseccion: PropTypes.func.isRequired,
};

function DocsTabUploadFormSection({
  uploadBusy,
  uploadStatus,
  uploadProgress,
  lastUploadError,
  lastAttemptedFileRef,
  contentDisabled,
  sinPestañas,
  bloqueSeleccionado,
  pestanasCurso,
  tipoInsert,
  setTipoInsert,
  rutaPadreKey,
  setRutaPadreKey,
  rutaDestinoKey,
  setRutaDestinoKey,
  subseccion,
  setSubseccion,
  posicion,
  setPosicion,
  bloquesContenido,
  archivoSeleccionado,
  isDragging,
  onDropZoneDrop,
  onDropZoneDragOver,
  onDropZoneDragLeave,
  onArchivoChange,
  setBloqueSeleccionado,
  handleAddDoc,
  addDocButtonTitle,
}) {
  const fileInputDisabled = contentDisabled || uploadBusy;
  const dropZoneAriaLabel = uploadBusy
    ? "Subida en curso; zona de arrastrar y soltar desactivada temporalmente"
    : "Zona para arrastrar y soltar un archivo";

  let fileInputTitle;
  if (contentDisabled) {
    fileInputTitle = TITLE_MANAGE_CONTENT;
  } else if (uploadBusy) {
    fileInputTitle = "Subida en curso; espera para elegir otro archivo";
  } else {
    fileInputTitle = undefined;
  }

  let fileInputAriaLabel;
  if (contentDisabled) {
    fileInputAriaLabel = "Seleccionar archivo de material (requiere permiso)";
  } else if (uploadBusy) {
    fileInputAriaLabel = "Subida en curso; selección de archivo desactivada temporalmente";
  } else {
    fileInputAriaLabel = "Seleccionar archivo de material";
  }

  const namedPestanas = getNamedCourseTabs(pestanasCurso);

  return (
    <section className="cc-doc-upload-panel cc-docs-upload-card" aria-labelledby="editar-docs-subir-heading">
      <div className="cc-doc-upload-header">
        <h2 id="editar-docs-subir-heading" className="cc-docs-upload-card-title">
          Añadir un archivo nuevo
        </h2>
      </div>
      <p className="cc-doc-hint cc-docs-upload-card-lead">
        Sigue el orden de los pasos. El archivo se sube al servidor y se enlaza al nodo del temario que elijas.
      </p>

      {uploadBusy && (
        <output className="cc-docs-tab-busy-msg" aria-live="polite">
          Subida en curso…
        </output>
      )}

      <div className="cc-edit-docs-step-row">
        <span className="cc-edit-docs-step-badge" aria-hidden="true">
          1
        </span>
        <div className="cc-edit-docs-step-body">
          <label htmlFor="editar-docs-pestaña">Pestaña del curso</label>
          <p className="cc-docs-field-hint">Cada pestaña es una sección distinta para el alumno.</p>
          <ViewportSelect
            id="editar-docs-pestaña"
            className="form-control"
            value={bloqueSeleccionado}
            onChange={(ev) => setBloqueSeleccionado(ev.target.value)}
            disabled={contentDisabled}
            title={contentDisabled ? TITLE_MANAGE_CONTENT : undefined}
          >
            <option value="">— Elige primero la pestaña —</option>
            {namedPestanas.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </ViewportSelect>
        </div>
      </div>

      <div className="cc-edit-docs-step-row">
        <span className="cc-edit-docs-step-badge" aria-hidden="true">
          2
        </span>
        <div className="cc-edit-docs-step-body">
          <label htmlFor="editar-docs-tipo-insert">¿Dónde colocarlo en el temario?</label>
          <ViewportSelect
            id="editar-docs-tipo-insert"
            className="form-control"
            value={tipoInsert}
            onChange={(ev) => setTipoInsert(ev.target.value)}
            disabled={contentDisabled || !bloqueSeleccionado}
            title={contentDisabled ? TITLE_MANAGE_CONTENT : undefined}
          >
            <option value="nuevo">Crear un apartado nuevo (tema, unidad o bloque)</option>
            <option value="existente">Añadirlo a un apartado que ya existe</option>
          </ViewportSelect>
        </div>
      </div>

      <DocsTabTemarioPlacementFields
        tipoInsert={tipoInsert}
        contentDisabled={contentDisabled}
        bloqueSeleccionado={bloqueSeleccionado}
        bloquesContenido={bloquesContenido}
        rutaPadreKey={rutaPadreKey}
        setRutaPadreKey={setRutaPadreKey}
        rutaDestinoKey={rutaDestinoKey}
        setRutaDestinoKey={setRutaDestinoKey}
        subseccion={subseccion}
        setSubseccion={setSubseccion}
      />

      <div className="cc-edit-docs-step-row">
        <span className="cc-edit-docs-step-badge" aria-hidden="true">
          3
        </span>
        <div className="cc-edit-docs-step-body">
          <label htmlFor="editar-docs-posicion">Orden dentro del apartado</label>
          <ViewportSelect
            id="editar-docs-posicion"
            className="form-control"
            value={posicion}
            onChange={(ev) => setPosicion(ev.target.value)}
            disabled={contentDisabled}
            title={contentDisabled ? TITLE_MANAGE_CONTENT : undefined}
          >
            <option value="debajo">{getMessage("courseEditor.testsTemario.insertAtEnd")}</option>
            <option value="encima">{getMessage("courseEditor.testsTemario.insertAtStart")}</option>
          </ViewportSelect>
        </div>
      </div>

      <div className="cc-edit-docs-step-row">
        <span className="cc-edit-docs-step-badge" aria-hidden="true">
          4
        </span>
        <div className="cc-edit-docs-step-body">
          <span className="cc-docs-step-label">Archivo (PDF o otros formatos admitidos)</span>
          <input
            id="editar-docs-archivo"
            className="cc-edit-docs-file-hidden"
            type="file"
            accept={DOC_UPLOAD_ACCEPT_ATTR}
            onChange={onArchivoChange}
            disabled={fileInputDisabled}
            title={fileInputTitle}
            aria-label={fileInputAriaLabel}
            aria-busy={uploadBusy}
            aria-disabled={fileInputDisabled}
          />
          <section
            aria-label={dropZoneAriaLabel}
            aria-busy={uploadBusy}
            onDrop={onDropZoneDrop}
            onDragOver={onDropZoneDragOver}
            onDragLeave={onDropZoneDragLeave}
            className={`cc-edit-docs-upload-zone ${isDragging && !uploadBusy ? "is-dragging" : ""} ${contentDisabled ? "is-disabled" : ""} ${uploadBusy ? "is-busy" : ""}`}
          >
            <label htmlFor="editar-docs-archivo" className="cc-edit-docs-upload-zone-label">
              <span className="cc-edit-docs-upload-cta">
                <strong>Haz clic</strong> o arrastra el archivo aquí
              </span>
              <span className="cc-edit-docs-upload-meta">
                PDF, Office, audio o vídeo · {getMessage("courseEditor.maxFileSizeHint", {
                  maxSize: getCourseMaterialMaxFileLabel(),
                })}
              </span>
            </label>
          </section>
          <p className="cc-edit-docs-file-summary" aria-live="polite">
            {archivoSeleccionado && archivoSeleccionado instanceof File ? (
              <>
                Listo para subir: <strong>{archivoSeleccionado.name}</strong>
                {formatFileSize(archivoSeleccionado.size) ? ` · ${formatFileSize(archivoSeleccionado.size)}` : ""}
              </>
            ) : (
              <span className="cc-edit-docs-file-summary-empty">Sin archivo seleccionado todavía.</span>
            )}
          </p>
        </div>
      </div>

      <div className="cc-edit-docs-primary-wrap">
        <button
          type="button"
          className="style1-button cc-doc-primary-btn"
          onClick={() => {
            handleAddDoc();
          }}
          disabled={
            uploadBusy ||
            contentDisabled ||
            sinPestañas ||
            !bloqueSeleccionado ||
            !(archivoSeleccionado instanceof File)
          }
          title={addDocButtonTitle}
        >
          Subir y asociar al temario
        </button>
        {contentDisabled && (
          <span className="cc-docs-tab-inline-note">No tienes permiso para añadir documentos en este curso.</span>
        )}
      </div>

      {uploadStatus !== "idle" && (
        <div className="cc-docs-upload-progress">
          <div className="cc-docs-upload-progress-track">
            <div className="cc-docs-upload-progress-fill" style={{ width: `${uploadProgress}%` }} />
          </div>
          <p className="cc-docs-upload-progress-text">{uploadPhaseMessage(uploadStatus, uploadProgress, lastUploadError)}</p>
          {uploadStatus === "error" && (
            <button
              type="button"
              className="style2-button"
              onClick={() => {
                handleAddDoc(lastAttemptedFileRef.current);
              }}
              disabled={!lastAttemptedFileRef.current || contentDisabled}
              title={contentDisabled ? TITLE_MANAGE_CONTENT : undefined}
            >
              Reintentar subida
            </button>
          )}
        </div>
      )}
    </section>
  );
}

DocsTabUploadFormSection.propTypes = {
  uploadBusy: PropTypes.bool.isRequired,
  uploadStatus: PropTypes.string.isRequired,
  uploadProgress: PropTypes.number.isRequired,
  lastUploadError: PropTypes.string.isRequired,
  lastAttemptedFileRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
  contentDisabled: PropTypes.bool.isRequired,
  sinPestañas: PropTypes.bool.isRequired,
  bloqueSeleccionado: PropTypes.string.isRequired,
  pestanasCurso: PropTypes.arrayOf(PropTypes.string).isRequired,
  tipoInsert: PropTypes.string.isRequired,
  setTipoInsert: PropTypes.func.isRequired,
  rutaPadreKey: PropTypes.string.isRequired,
  setRutaPadreKey: PropTypes.func.isRequired,
  rutaDestinoKey: PropTypes.string.isRequired,
  setRutaDestinoKey: PropTypes.func.isRequired,
  subseccion: PropTypes.string.isRequired,
  setSubseccion: PropTypes.func.isRequired,
  posicion: PropTypes.string.isRequired,
  setPosicion: PropTypes.func.isRequired,
  bloquesContenido: PropTypes.objectOf(PropTypes.array).isRequired,
  archivoSeleccionado: PropTypes.oneOfType([PropTypes.instanceOf(File), PropTypes.oneOf([null])]),
  isDragging: PropTypes.bool.isRequired,
  onDropZoneDrop: PropTypes.func.isRequired,
  onDropZoneDragOver: PropTypes.func.isRequired,
  onDropZoneDragLeave: PropTypes.func.isRequired,
  onArchivoChange: PropTypes.func.isRequired,
  setBloqueSeleccionado: PropTypes.func.isRequired,
  handleAddDoc: PropTypes.func.isRequired,
  addDocButtonTitle: PropTypes.string,
};

DocsTabUploadFormSection.defaultProps = {
  archivoSeleccionado: null,
  addDocButtonTitle: undefined,
};

function DocsTabLowerPanels({
  contenidoDescargable,
  setContenidoDescargable,
  instructorLocked,
  bloqueSeleccionado,
  bloquesContenido,
  setBloquesContenido,
  contentDisabled,
  pestanasCurso,
  onArchivoOpen,
  onArchivoDownload,
  onArchivoRemove,
  courseTests,
  setTests,
}) {
  return (
    <>
      <div className="cc-docs-panel-secondary">
        <h2 className="cc-docs-section-heading">Opciones para los alumnos</h2>
        <p className="cc-docs-field-hint cc-docs-panel-secondary-lead">
          Afecta cómo ven los materiales al estudiar (no cambia cómo los subes tú).
        </p>
        <CourseDownloadableToggle
          headingId="editar-curso-docs-descarga-heading"
          checked={contenidoDescargable}
          onChange={setContenidoDescargable}
          disabled={instructorLocked}
        />
        <p className="cc-docs-tab-footnote">Guarda con &quot;Guardar cambios&quot; en el menú lateral para aplicar esta preferencia.</p>
      </div>

      <section className="cc-docs-manage-section" aria-labelledby="editar-docs-temario-heading">
        <h2 id="editar-docs-temario-heading" className="cc-docs-section-heading">
          Temario de la pestaña seleccionada
        </h2>
        <p className="cc-docs-section-text">
          {getMessage("courseEditor.testsTemario.docsTabHint")}
        </p>
        <p className="cc-docs-section-text">
          {getMessage("courseEditor.documentActionsHint")}
        </p>
        {!bloqueSeleccionado && (
          <p className="cc-docs-tab-muted">Selecciona una pestaña en el paso 1 para editar su temario aquí.</p>
        )}
        <TemarioContenidoEditor
          nodes={bloqueSeleccionado ? bloquesContenido[bloqueSeleccionado] || [] : []}
          onNodesChange={(next) => {
            if (!bloqueSeleccionado) {
              return;
            }
            setBloquesContenido((prev) => ({ ...prev, [bloqueSeleccionado]: next }));
          }}
          disabled={contentDisabled}
          selectedTabLabel={bloqueSeleccionado}
          onArchivoOpen={onArchivoOpen}
          onArchivoDownload={onArchivoDownload}
          onArchivoRemove={onArchivoRemove}
          courseTests={courseTests}
        />
        <AdminDocsTestsByTab
          tests={courseTests}
          pestanasCurso={pestanasCurso}
          contenido={bloquesContenido}
          onTestsChange={setTests}
          disabled={contentDisabled}
        />
      </section>

      <section className="cc-docs-summary-section" aria-labelledby="editar-docs-resumen-heading">
        <h2 id="editar-docs-resumen-heading" className="cc-docs-section-heading">
          Resumen de todas las pestañas
        </h2>
        <p className="cc-docs-section-text">
          {getMessage("courseEditor.documentSummaryHint")}
        </p>
        {getNamedCourseTabs(pestanasCurso).map((p) => {
          const subs = bloquesContenido[p] || [];
          if (subs.length === 0) {
            return null;
          }
          return (
            <div key={p} className="cc-docs-summary-block">
              <h3 className="cc-docs-summary-tab-title">{p}</h3>
              <TemarioContenidoPreview
                nodes={subs}
                onArchivoClick={onArchivoOpen}
                onArchivoDownload={onArchivoDownload}
              />
            </div>
          );
        })}
        <p className="cc-docs-tab-save-reminder">💾 Recuerda pulsar &quot;Guardar cambios&quot; en el menú lateral para aplicar los cambios.</p>
      </section>
    </>
  );
}

DocsTabLowerPanels.propTypes = {
  contenidoDescargable: PropTypes.bool.isRequired,
  setContenidoDescargable: PropTypes.func.isRequired,
  instructorLocked: PropTypes.bool.isRequired,
  bloqueSeleccionado: PropTypes.string.isRequired,
  bloquesContenido: PropTypes.objectOf(PropTypes.array).isRequired,
  setBloquesContenido: PropTypes.func.isRequired,
  contentDisabled: PropTypes.bool.isRequired,
  pestanasCurso: PropTypes.arrayOf(PropTypes.string).isRequired,
  onArchivoOpen: PropTypes.func.isRequired,
  onArchivoDownload: PropTypes.func.isRequired,
  onArchivoRemove: PropTypes.func,
  courseTests: PropTypes.arrayOf(PropTypes.object),
  setTests: PropTypes.func,
};

DocsTabLowerPanels.defaultProps = {
  onArchivoRemove: undefined,
  courseTests: [],
  setTests: undefined,
};

export default function EditarCursoDocsTab({
  rol,
  rolReady,
  archivoSeleccionado,
  contenidoDescargable,
  setContenidoDescargable,
  uploadStatus,
  uploadProgress,
  lastUploadError,
  setArchivoSeleccionado,
  setUploadProgress,
  setUploadStatus,
  setLastUploadError,
  setMensaje,
  canManageContent,
  bloqueSeleccionado,
  setBloqueSeleccionado,
  pestanasCurso,
  tipoInsert,
  setTipoInsert,
  rutaPadreKey,
  setRutaPadreKey,
  rutaDestinoKey,
  setRutaDestinoKey,
  subseccion,
  setSubseccion,
  posicion,
  setPosicion,
  bloquesContenido,
  setBloquesContenido,
  handleAddDoc,
  lastAttemptedFileRef,
  courseTests,
  setTests,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const {
    embedArchivo,
    embedOpen,
    openCourseMaterialEmbed,
    closeCourseMaterialEmbed,
  } = useCourseMaterialEmbed();
  const contentDisabled = canManageContent === false;
  const instructorLocked = rol === "instructor";
  const uploadBusy = uploadStatus === "reading" || uploadStatus === "uploading";
  const namedPestanas = getNamedCourseTabs(pestanasCurso);
  const unnamedTabsPresent = hasUnnamedCourseTabs(pestanasCurso);
  const sinPestañas = namedPestanas.length === 0;

  const resetUploadUiAfterSelectionReject = () => {
    setUploadProgress(0);
    setUploadStatus("idle");
    setLastUploadError("");
    lastAttemptedFileRef.current = null;
  };

  const applySelectedFile = (f) => {
    if (uploadBusy) {
      setMensaje("⚠️ Espera a que termine la subida en curso antes de elegir otro archivo.");
      return false;
    }
    if (!f) {
      setArchivoSeleccionado(null);
      resetUploadUiAfterSelectionReject();
      return true;
    }
    if (isCourseMaterialFileOversized(f)) {
      const msg = getCourseMaterialOversizedSelectionMessage();
      setArchivoSeleccionado(null);
      lastAttemptedFileRef.current = null;
      setUploadProgress(0);
      setUploadStatus("error");
      setLastUploadError(msg);
      setMensaje(`⚠️ ${msg}`);
      return false;
    }
    if (!fileMatchesDocUploadAccept(f)) {
      setMensaje(
        "⚠️ Formato no admitido. Usa PDF, Office, audio o vídeo (mismos tipos que en el selector de archivo).",
      );
      setArchivoSeleccionado(null);
      resetUploadUiAfterSelectionReject();
      return false;
    }
    setArchivoSeleccionado(f);
    setUploadProgress(0);
    setUploadStatus("idle");
    setLastUploadError("");
    lastAttemptedFileRef.current = null;
    return true;
  };

  const onArchivoChange = (e) => {
    const f = e.target.files?.[0];
    if (uploadBusy) {
      setMensaje("⚠️ Espera a que termine la subida en curso antes de elegir otro archivo.");
      e.target.value = "";
      return;
    }
    const accepted = applySelectedFile(f);
    if (!accepted) {
      e.target.value = "";
    }
  };

  const onDropZoneDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (contentDisabled) {
      return;
    }
    if (uploadBusy) {
      setMensaje("⚠️ Espera a que termine la subida en curso antes de elegir otro archivo.");
      return;
    }
    const f = e.dataTransfer.files?.[0];
    if (f) {
      applySelectedFile(f);
    }
  };

  const onDropZoneDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!contentDisabled && !uploadBusy) {
      setIsDragging(true);
    }
  };

  const onDropZoneDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  let addDocButtonTitle;
  if (contentDisabled) {
    addDocButtonTitle = TITLE_MANAGE_CONTENT;
  } else if (sinPestañas || !bloqueSeleccionado) {
    addDocButtonTitle = "Selecciona una pestaña o crea pestañas en Datos principales";
  } else if (archivoSeleccionado instanceof File) {
    addDocButtonTitle = undefined;
  } else {
    addDocButtonTitle = "Selecciona un archivo (clic o arrastrar) antes de subir";
  }

  const quitarArchivoDelTemario = (path, archivoIndex) => {
    removeArchivoFromTemarioState({
      contentDisabled,
      bloqueSeleccionado,
      bloquesContenido,
      path,
      archivoIndex,
      setBloquesContenido,
      setMensaje,
    });
  };

  const descargarArchivoDelTemario = async (archivo) => {
    const downloaded = await downloadCourseMaterial(archivo);
    if (!downloaded) {
      setMensaje(`⚠️ ${getMessage("courseEditor.documentDownloadFailed")}`);
    }
  };

  if (!rolReady) {
    return (
      <>
        <h1>Documentos</h1>
        <p className="cc-message">Verificando permisos…</p>
      </>
    );
  }

  return (
    <>
      <DocsTabIntroHeader />

      {sinPestañas && pestanasCurso.length === 0 && (
        <div className="cc-docs-tab-alert" role="alert">
          <strong>Falta configurar pestañas.</strong> Ve a <strong>Datos principales</strong>, define al menos una pestaña con
          nombre, guarda, y vuelve aquí.
        </div>
      )}
      {sinPestañas && pestanasCurso.length > 0 && (
        <div className="cc-docs-tab-alert" role="alert">
          {getMessage("courseEditor.unnamedTabsAllEmptyHint")}
        </div>
      )}
      {unnamedTabsPresent && namedPestanas.length > 0 && (
        <p className="cc-docs-field-hint" style={{ color: "#ffc107" }}>
          {getMessage("courseEditor.unnamedTabsSelectHint")}
        </p>
      )}

      <DocsTabUploadFormSection
        uploadBusy={uploadBusy}
        uploadStatus={uploadStatus}
        uploadProgress={uploadProgress}
        lastUploadError={lastUploadError}
        lastAttemptedFileRef={lastAttemptedFileRef}
        contentDisabled={contentDisabled}
        sinPestañas={sinPestañas}
        bloqueSeleccionado={bloqueSeleccionado}
        pestanasCurso={pestanasCurso}
        tipoInsert={tipoInsert}
        setTipoInsert={setTipoInsert}
        rutaPadreKey={rutaPadreKey}
        setRutaPadreKey={setRutaPadreKey}
        rutaDestinoKey={rutaDestinoKey}
        setRutaDestinoKey={setRutaDestinoKey}
        subseccion={subseccion}
        setSubseccion={setSubseccion}
        posicion={posicion}
        setPosicion={setPosicion}
        bloquesContenido={bloquesContenido}
        archivoSeleccionado={archivoSeleccionado}
        isDragging={isDragging}
        onDropZoneDrop={onDropZoneDrop}
        onDropZoneDragOver={onDropZoneDragOver}
        onDropZoneDragLeave={onDropZoneDragLeave}
        onArchivoChange={onArchivoChange}
        setBloqueSeleccionado={setBloqueSeleccionado}
        handleAddDoc={handleAddDoc}
        addDocButtonTitle={addDocButtonTitle}
      />

      <DocsTabLowerPanels
        contenidoDescargable={contenidoDescargable}
        setContenidoDescargable={setContenidoDescargable}
        instructorLocked={instructorLocked}
        bloqueSeleccionado={bloqueSeleccionado}
        bloquesContenido={bloquesContenido}
        setBloquesContenido={setBloquesContenido}
        contentDisabled={contentDisabled}
        pestanasCurso={pestanasCurso}
        onArchivoOpen={openCourseMaterialEmbed}
        onArchivoDownload={descargarArchivoDelTemario}
        onArchivoRemove={contentDisabled ? undefined : quitarArchivoDelTemario}
        courseTests={courseTests}
        setTests={setTests}
      />

      <CourseMaterialEmbedModal
        open={embedOpen}
        archivo={embedArchivo}
        onClose={closeCourseMaterialEmbed}
        canDownload
      />
    </>
  );
}

EditarCursoDocsTab.propTypes = {
  rol: PropTypes.string.isRequired,
  rolReady: PropTypes.bool.isRequired,
  archivoSeleccionado: PropTypes.oneOfType([PropTypes.instanceOf(File), PropTypes.oneOf([null])]),
  contenidoDescargable: PropTypes.bool.isRequired,
  setContenidoDescargable: PropTypes.func.isRequired,
  uploadStatus: PropTypes.string.isRequired,
  uploadProgress: PropTypes.number.isRequired,
  lastUploadError: PropTypes.string.isRequired,
  setArchivoSeleccionado: PropTypes.func.isRequired,
  setUploadProgress: PropTypes.func.isRequired,
  setUploadStatus: PropTypes.func.isRequired,
  setLastUploadError: PropTypes.func.isRequired,
  setMensaje: PropTypes.func.isRequired,
  canManageContent: PropTypes.bool.isRequired,
  bloqueSeleccionado: PropTypes.string.isRequired,
  setBloqueSeleccionado: PropTypes.func.isRequired,
  pestanasCurso: PropTypes.arrayOf(PropTypes.string).isRequired,
  tipoInsert: PropTypes.string.isRequired,
  setTipoInsert: PropTypes.func.isRequired,
  rutaPadreKey: PropTypes.string.isRequired,
  setRutaPadreKey: PropTypes.func.isRequired,
  rutaDestinoKey: PropTypes.string.isRequired,
  setRutaDestinoKey: PropTypes.func.isRequired,
  subseccion: PropTypes.string.isRequired,
  setSubseccion: PropTypes.func.isRequired,
  posicion: PropTypes.string.isRequired,
  setPosicion: PropTypes.func.isRequired,
  bloquesContenido: PropTypes.objectOf(PropTypes.array).isRequired,
  setBloquesContenido: PropTypes.func.isRequired,
  handleAddDoc: PropTypes.func.isRequired,
  lastAttemptedFileRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
  courseTests: PropTypes.arrayOf(PropTypes.object),
  setTests: PropTypes.func,
};

EditarCursoDocsTab.defaultProps = {
  archivoSeleccionado: null,
  courseTests: [],
  setTests: undefined,
};
